import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      org: null,
      token: null,
      deviceToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      otpChallenge: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null, otpChallenge: null });
        try {
          const response = await axios.post(`${API}/auth/login`, {
            email, password, device_token: get().deviceToken || null,
          });
          if (response.data.otp_required) {
            set({
              isLoading: false,
              otpChallenge: { id: response.data.challenge_id, email: response.data.email_masked },
            });
            return { success: false, otpRequired: true };
          }
          const { token, user, tenant, org } = response.data;
          set({ token, user, tenant, org: org || null, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Falha no login';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      verifyOtp: async (code, trustDevice) => {
        const challenge = get().otpChallenge;
        if (!challenge) return { success: false, error: 'Sessão de verificação expirada' };
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post(`${API}/auth/verify-otp`, {
            challenge_id: challenge.id, code, trust_device: !!trustDevice,
          });
          const { token, user, tenant, org, device_token } = response.data;
          set({
            token, user, tenant, org: org || null, isAuthenticated: true, isLoading: false,
            otpChallenge: null, deviceToken: device_token || get().deviceToken,
          });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Código inválido';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      resendOtp: async () => {
        const challenge = get().otpChallenge;
        if (!challenge) return { success: false };
        try {
          const response = await axios.post(`${API}/auth/resend-otp`, { challenge_id: challenge.id });
          set({ otpChallenge: { ...challenge, id: response.data.challenge_id } });
          return { success: true };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Erro ao reenviar código' };
        }
      },

      cancelOtp: () => set({ otpChallenge: null, error: null }),

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post(`${API}/auth/register`, data);
          const { token, user, tenant } = response.data;
          set({ token, user, tenant, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Registration failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      logout: () => {
        set({ user: null, tenant: null, org: null, token: null, isAuthenticated: false, error: null, otpChallenge: null });
      },

      getAuthHeader: () => {
        const token = get().token;
        return token ? { Authorization: `Bearer ${token}` } : {};
      },

      isClientUser: () => get().user?.role === 'client',

      checkAuth: async () => {
        const token = get().token;
        if (!token) return;

        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          set({ user: response.data.user, tenant: response.data.tenant, org: response.data.org || null });
        } catch (error) {
          if (error.response?.status === 401) {
            get().logout();
          }
        }
      }
    }),
    {
      name: 'oneview-auth',
      partialize: (state) => ({
        token: state.token, user: state.user, tenant: state.tenant, org: state.org,
        deviceToken: state.deviceToken, isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
